import React, { useEffect, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { authSlice, toastSlice } from 'slices';
import { InviteUsersContainer } from 'components/Modal/InviteUsers/InviteUsersContainer';
import { useTranslation } from 'react-i18next';
import { DealersDetailViewContainer } from 'pages/Shared/Dealer/DealerDetailView/DealersDetailViewContainer';
import { IDealerItem, IDealerObject } from 'pages/Shared/Dealer/Models/dealers';
import { useDealerRole, useManagerRole } from 'hooks/role';
import ManagerLayout from 'components/Layout/Manager/ManagerLayout';
import DealerLayout from 'components/Layout/Dealer/DealerLayout';
import { useGetDealerListQuery } from 'thunks/RTKQuery/dealer';
import { Spinner2 } from 'components';
import { DealerListViewContainer } from './DealerListView/DealersListViewContainer';

export const DealersView: React.FC = () => {
  const dispatch = useAppDispatch();
  const [selectedDealer, setSelectedDealer] = useState<IDealerItem>();
  const [dealerListData, setDealerListData] = React.useState<IDealerItem[]>([]);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const { data: authData } = useAppSelector((state) => state.auth);
  const rootDealer = authData?.dealer;
  const [errorString, setErrorString] = useState('');
  const [showList, setShowList] = useState<boolean>(true);
  const { t } = useTranslation();

  const isDealer = useDealerRole();
  const isManager = useManagerRole();
  const createDealer = () => {
    setSelectedDealer(undefined);
  };

  const inviteUser = () => {
    dispatch(authSlice.actions.showInviteUserModel());
  };

  const handleDealerSelection = (dealer: IDealerItem) => {
    // If selecting the same dealer, force a refresh
    if (selectedDealer?.id === dealer.id) {
      setRefreshKey((prev) => prev + 1);
    }
    setSelectedDealer(dealer);
  };

  const appendListViewData = (data: IDealerItem) => {
    if (data) {
      const existingItemIndex = dealerListData.findIndex(
        (x) => x.id === data.id
      );
      if (existingItemIndex !== -1) {
        const updateData = dealerListData.filter((x) => x.id !== data.id);
        updateData.splice(existingItemIndex, 0, data);

        setDealerListData(updateData);
        return;
      }

      if ([data, ...dealerListData].length > 1) {
        setShowList(true);
      }
      setDealerListData([data, ...dealerListData]);
    }
  };

  const params = 'id,name,dealerId,email,userId,parentDealerId';
  const {
    data: dealerData = [],
    isLoading: isDealerDataLoading,
    error: dealerDataError,
  } = useGetDealerListQuery(
    !isDealer ? params : `${params}&dealerId=${rootDealer?.dealerId}`
  );

  useEffect(() => {
    if (!dealerData || !dealerData.length) {
      setShowList(false);
      return;
    }
    setShowList(dealerData.length > 0);
    setDealerListData(dealerData);
    const list = dealerData as IDealerObject[];
    if (list && list.length > 0 && selectedDealer === undefined) {
      setSelectedDealer(list[0]);
    }
  }, [dealerData]);

  useEffect(() => {
    if (dealerDataError && 'message' in dealerDataError) {
      setErrorString(`${dealerDataError.message}`);
    }
  }, [dealerDataError]);

  useEffect(() => {
    if (errorString !== '') {
      dispatch(
        toastSlice.actions.show({
          title: t('contract.error'),
          message: errorString,
        })
      );
    }
    setErrorString('');
  }, [errorString]);
  const Layout = isManager ? ManagerLayout : DealerLayout;
  const canCreateDealer =
    isManager || (rootDealer && rootDealer?.permissions?.subDealerAllowed);

  return (
    <Layout>
      <Spinner2 show={isDealerDataLoading} />
      <h1 className="ml-2 mb-4 mt-3">{t('dealerManagement.heading')}</h1>
      <hr />
      <Row>
        <Col md={showList ? 4 : 2}>
          <Row>
            {showList && (
              <Col md={12}>
                <DealerListViewContainer
                  setSelectedDealer={handleDealerSelection}
                  dealerData={dealerListData}
                  selectedDealer={selectedDealer}
                />
              </Col>
            )}
          </Row>
        </Col>
        <Col md={8}>
          <Row>
            <Col md={6} className="d-flex justify-content-start">
              <Button
                onClick={inviteUser}
                className={
                  !showList
                    ? 'btn-outline-primary invite-button-hide-list'
                    : 'btn-outline-primary'
                }
              >
                {selectedDealer?.userId
                  ? t('dealerManagement.reInvite')
                  : t('dealerManagement.invitation')}
              </Button>
            </Col>
            <Col md={6} className="d-flex justify-content-end">
              {canCreateDealer && (
                <Button onClick={createDealer} className="btn-outline-primary">
                  {t('dealerManagement.newUser')}
                </Button>
              )}
            </Col>
          </Row>
          <Row className="mt-3">
            <Col md={12}>
              <DealersDetailViewContainer
                key={`${selectedDealer?.id || 'new'}-${refreshKey}`}
                setErrorString={setErrorString}
                selectedDealer={selectedDealer}
                setSelectedDealer={handleDealerSelection}
                appendListViewData={appendListViewData}
                rootDealer={rootDealer}
              />
            </Col>
          </Row>
        </Col>
      </Row>

      <InviteUsersContainer
        setErrorString={setErrorString}
        selectedDealer={selectedDealer}
        setSelectedDealer={handleDealerSelection}
        appendListViewData={appendListViewData}
      />
    </Layout>
  );
};
